import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  InputLabel,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, ChevronLeft, ChevronRight, Calculate, Assignment, CloudUpload, OpenInNew, Add, Delete } from '@mui/icons-material';
import {
  getFulfillmentRequests,
  getFulfillmentRequest,
  updateFulfillmentRequest,
  updateFulfillmentRequestItem,
  uploadInspectionImage,
  uploadPackageImage,
  uploadFulfillmentRequestItemReceivedImage,
  recalculateFees
} from '../../services/fulfillment';

const Requests = () => {
  const { t } = useTranslation(['fulfillment', 'common']);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [recalculateDialog, setRecalculateDialog] = useState({ open: false, request: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processDrawer, setProcessDrawer] = useState({ open: false, sid: null });
  const [requestDetail, setRequestDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [uploadingInspection, setUploadingInspection] = useState(false);
  const [uploadingPackage, setUploadingPackage] = useState(false);
  const [uploadingItemImage, setUploadingItemImage] = useState({});
  const [formStatus, setFormStatus] = useState('');
  const [formAdminNotes, setFormAdminNotes] = useState('');
  const [formPackageWeight, setFormPackageWeight] = useState('');
  const [formPackageDimensions, setFormPackageDimensions] = useState('');
  const createEmptyRequestItem = () => ({
    sid: null,
    tracking_number: '',
    local_logistics_company_name: '',
    items_count: '',
    package_count: '1',
    package_weight: '',
    package_dimensions: '',
    goods_type: 'normal',
    condition: 'new',
    properties: [],
    seller_name: '',
    seller_platform: '',
    seller_order_number: '',
    status: 'pending_receive',
    received_images: [],
  });
  const [formItems, setFormItems] = useState([createEmptyRequestItem()]);

  const normalizeRequestItemsForForm = (data) => {
    const items = Array.isArray(data.items) && data.items.length ? data.items : [{
      sid: null,
      tracking_number: data.tracking_number || '',
      local_logistics_company_name: data.local_logistics_company_name || '',
      items_count: data.items_count != null ? String(data.items_count) : '',
      package_count: data.package_count != null ? String(data.package_count) : '1',
      package_weight: data.package_weight != null ? String(data.package_weight) : '',
      package_dimensions: data.package_dimensions || '',
      goods_type: data.goods_type || 'normal',
      condition: data.condition || 'new',
      properties: data.properties || [],
      seller_name: data.seller_name || '',
      seller_platform: data.seller_platform || '',
      seller_order_number: data.seller_order_number || '',
      status: data.status === 'received' ? 'received' : 'pending_receive',
      received_images: [],
    }];
    return items.map((item) => ({
      sid: item.sid || null,
      tracking_number: item.tracking_number || '',
      local_logistics_company_name: item.local_logistics_company_name || '',
      items_count: item.items_count != null ? String(item.items_count) : '',
      package_count: item.package_count != null ? String(item.package_count) : '1',
      package_weight: item.package_weight != null ? String(item.package_weight) : '',
      package_dimensions: item.package_dimensions || '',
      goods_type: item.goods_type || 'normal',
      condition: item.condition || 'new',
      properties: item.properties || [],
      seller_name: item.seller_name || '',
      seller_platform: item.seller_platform || '',
      seller_order_number: item.seller_order_number || '',
      status: item.status || 'pending_receive',
      received_images: item.received_images || [],
    }));
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  const getUserIdentity = (user) => {
    if (!user) return 'N/A';
    return user.display_name || user.username || user.email || user.phone || 'N/A';
  };

  const getUserSecondaryIdentity = (user) => {
    if (!user) return '';
    const contacts = [user.email, user.phone].filter(Boolean);
    return contacts.find((contact) => contact !== getUserIdentity(user)) || '';
  };

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const response = await getFulfillmentRequests(params);
      if (response.results) {
        setRequests(response.results);
        setTotalItems(response.count || 0);
      } else if (Array.isArray(response)) {
        setRequests(response);
        setTotalItems(response.length);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load requests',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (processDrawer.open && processDrawer.sid) {
      setDetailLoading(true);
      getFulfillmentRequest(processDrawer.sid)
        .then((data) => {
          setRequestDetail(data);
          setFormStatus(data.status || '');
          setFormAdminNotes(data.admin_notes || '');
          setFormPackageWeight(data.package_weight != null ? String(data.package_weight) : '');
          setFormPackageDimensions(data.package_dimensions || '');
          setFormItems(normalizeRequestItemsForForm(data));
        })
        .catch((err) => {
          setSnackbar({ open: true, message: err.response?.data?.error || t('fulfillment:requests.messages.failed'), severity: 'error' });
          setProcessDrawer({ open: false, sid: null });
        })
        .finally(() => setDetailLoading(false));
    } else {
      setRequestDetail(null);
    }
  }, [processDrawer.open, processDrawer.sid, t]);

  const handleSaveStatus = async () => {
    if (!processDrawer.sid) return;
    if (formStatus === 'packaged') {
      const w = parseFloat(formPackageWeight);
      if (Number.isNaN(w) || w <= 0) {
        setSnackbar({ open: true, message: t('fulfillment:requests.messages.packageWeightRequired'), severity: 'error' });
        return;
      }
      if (!formPackageDimensions || !String(formPackageDimensions).trim()) {
        setSnackbar({ open: true, message: t('fulfillment:requests.messages.packageDimensionsRequired'), severity: 'error' });
        return;
      }
    }
    setSavingStatus(true);
    try {
      const payload = { status: formStatus, admin_notes: formAdminNotes || null };
      const normalizedItems = formItems.map((item) => ({
        ...(item.sid ? { sid: item.sid } : {}),
        tracking_number: String(item.tracking_number || '').trim(),
        local_logistics_company_name: String(item.local_logistics_company_name || '').trim() || null,
        items_count: parseInt(item.items_count, 10),
        package_count: parseInt(item.package_count, 10) || 1,
        package_weight: item.package_weight !== '' && item.package_weight != null ? parseFloat(item.package_weight) : null,
        package_dimensions: String(item.package_dimensions || '').trim() || null,
        goods_type: item.goods_type || 'normal',
        condition: item.condition || 'new',
        properties: item.properties || [],
        seller_name: String(item.seller_name || '').trim() || null,
        seller_platform: String(item.seller_platform || '').trim() || null,
        seller_order_number: String(item.seller_order_number || '').trim() || null,
        status: item.status || 'pending_receive',
        received_images: item.received_images || [],
      }));
      for (let index = 0; index < normalizedItems.length; index += 1) {
        const item = normalizedItems[index];
        if (!item.tracking_number || !Number.isFinite(item.items_count) || item.items_count < 1) {
          setSnackbar({ open: true, message: `Tracking item ${index + 1} needs a tracking number and valid item count`, severity: 'error' });
          setSavingStatus(false);
          return;
        }
      }
      payload.items = normalizedItems;
      if (formStatus === 'packaged') {
        payload.package_weight = parseFloat(formPackageWeight);
        payload.package_dimensions = String(formPackageDimensions).trim();
      }
      await updateFulfillmentRequest(processDrawer.sid, payload);
      setSnackbar({ open: true, message: t('fulfillment:requests.messages.updated'), severity: 'success' });
      const updated = await getFulfillmentRequest(processDrawer.sid);
      setRequestDetail(updated);
      setFormStatus(updated.status || formStatus);
      setFormItems(normalizeRequestItemsForForm(updated));
      loadRequests();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('fulfillment:requests.messages.failed'), severity: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  const updateFormItem = (index, updates) => {
    setFormItems((prev) => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...updates } : item
    )));
  };

  const addFormItem = () => {
    setFormItems((prev) => [...prev, createEmptyRequestItem()]);
  };

  const removeFormItem = (index) => {
    setFormItems((prev) => prev.length === 1 ? prev : prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSaveItemStatus = async (index) => {
    const item = formItems[index];
    if (!processDrawer.sid || !item?.sid) return;
    try {
      const response = await updateFulfillmentRequestItem(processDrawer.sid, item.sid, {
        status: item.status || 'pending_receive',
      });
      if (response.fulfillment_request) {
        setRequestDetail(response.fulfillment_request);
        setFormStatus(response.fulfillment_request.status || formStatus);
        setFormItems(normalizeRequestItemsForForm(response.fulfillment_request));
      }
      setSnackbar({ open: true, message: 'Tracking item status updated', severity: 'success' });
      loadRequests();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to update tracking item', severity: 'error' });
    }
  };

  const handleUploadItemReceivedImage = async (index, e) => {
    const file = e?.target?.files?.[0];
    const item = formItems[index];
    if (!file || !processDrawer.sid || !item?.sid) return;
    setUploadingItemImage((prev) => ({ ...prev, [item.sid]: true }));
    try {
      const response = await uploadFulfillmentRequestItemReceivedImage(processDrawer.sid, item.sid, file);
      updateFormItem(index, {
        status: response.item?.status || 'received',
        received_images: response.item?.received_images || [],
      });
      if (response.fulfillment_request) {
        setRequestDetail(response.fulfillment_request);
        setFormStatus(response.fulfillment_request.status || formStatus);
        setFormItems(normalizeRequestItemsForForm(response.fulfillment_request));
      }
      setSnackbar({ open: true, message: 'Receive proof uploaded', severity: 'success' });
      loadRequests();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to upload receive proof', severity: 'error' });
    } finally {
      setUploadingItemImage((prev) => ({ ...prev, [item.sid]: false }));
      if (e?.target) e.target.value = '';
    }
  };

  const handleUploadInspection = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !processDrawer.sid) return;
    setUploadingInspection(true);
    try {
      const result = await uploadInspectionImage(processDrawer.sid, file);
      if (result.inspection_images) {
        setRequestDetail((prev) => (prev ? { ...prev, inspection_images: result.inspection_images } : null));
      }
      setSnackbar({ open: true, message: 'Inspection image uploaded', severity: 'success' });
      e.target.value = '';
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('fulfillment:requests.messages.uploadFailed'), severity: 'error' });
    } finally {
      setUploadingInspection(false);
    }
  };

  const handleUploadPackage = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !processDrawer.sid) return;
    setUploadingPackage(true);
    try {
      const result = await uploadPackageImage(processDrawer.sid, file);
      if (result.package_image) {
        setRequestDetail((prev) => (prev ? { ...prev, package_image: result.package_image } : null));
      }
      setSnackbar({ open: true, message: 'Package image uploaded', severity: 'success' });
      e.target.value = '';
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('fulfillment:requests.messages.uploadFailed'), severity: 'error' });
    } finally {
      setUploadingPackage(false);
    }
  };

  const handleRecalculateClick = (request) => {
    setRecalculateDialog({ open: true, request });
  };

  const handleRecalculateConfirm = async () => {
    try {
      await recalculateFees(recalculateDialog.request.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:requests.messages.recalculated'),
        severity: 'success'
      });
      setRecalculateDialog({ open: false, request: null });
      loadRequests();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:requests.messages.failed'),
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending_payment': '#D97706',
      'pending_receive': '#0EA5E9',
      'received': '#22C55E',
      'ready_to_package': '#14B8A6',
      'inspecting': '#8B5CF6',
      'packaged': '#10B981',
      'completed': '#059669',
      'cancelled': '#DC143C'
    };
    return statusColors[status] || '#6B7280';
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('fulfillment:table.sn'), width: 80, sortable: false, filterable: false },
    {
      field: 'request_number',
      headerName: t('fulfillment:requests.requestNumber'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'user',
      headerName: t('fulfillment:requests.user'),
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {getUserIdentity(params.row.user)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {params.row.user?.bin_code ? `BIN: ${params.row.user.bin_code}` : 'BIN: N/A'}
            {getUserSecondaryIdentity(params.row.user) ? ` - ${getUserSecondaryIdentity(params.row.user)}` : ''}
          </Typography>
        </Box>
      )
    },
    {
      field: 'package_info',
      headerName: 'Package',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const packageInfo = params.row.package_info;
        if (!packageInfo) {
          return <Typography variant="body2" color="text.secondary">Pending</Typography>;
        }
        return (
          <Box sx={{ py: 0.5, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }} noWrap>
              {packageInfo.package_number}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {packageInfo.packaging_status || 'pending'}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'service_type',
      headerName: t('fulfillment:requests.serviceType'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.service_type?.name || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: t('fulfillment:requests.status'),
      width: 150,
      renderCell: (params) => {
        const statusKey = `fulfillment:requests.statuses.${params.value}`;
        const statusLabel = t(statusKey);
        return (
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              backgroundColor: `${getStatusColor(params.value)}20`,
              color: getStatusColor(params.value),
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    {
      field: 'tracking_number',
      headerName: t('fulfillment:requests.trackingNumber'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'items_count',
      headerName: t('fulfillment:requests.itemsCount'),
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'total_fee',
      headerName: t('fulfillment:requests.totalFee'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          ¥{Number(params.value).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'created_at',
      headerName: t('fulfillment:requests.createdAt'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('fulfillment:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('fulfillment:requests.processRequest')}>
            <IconButton
              color="primary"
              onClick={() => setProcessDrawer({ open: true, sid: params.row.sid })}
            >
              <Assignment />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('fulfillment:requests.recalculateFees')}>
            <IconButton
              color="primary"
              onClick={() => handleRecalculateClick(params.row)}
            >
              <Calculate />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('fulfillment:requests.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadRequests}
          disabled={loading}
          sx={{ minWidth: 'auto' }}
        >
          {t('common:refresh')}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending_payment">{t('fulfillment:requests.statuses.pending_payment')}</MenuItem>
                <MenuItem value="pending_receive">{t('fulfillment:requests.statuses.pending_receive')}</MenuItem>
                <MenuItem value="received">{t('fulfillment:requests.statuses.received')}</MenuItem>
                <MenuItem value="ready_to_package">{t('fulfillment:requests.statuses.ready_to_package')}</MenuItem>
                <MenuItem value="inspecting">{t('fulfillment:requests.statuses.inspecting')}</MenuItem>
                <MenuItem value="packaged">{t('fulfillment:requests.statuses.packaged')}</MenuItem>
                <MenuItem value="completed">{t('fulfillment:requests.statuses.completed')}</MenuItem>
                <MenuItem value="cancelled">{t('fulfillment:requests.statuses.cancelled')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {requests.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('fulfillment:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadRequests}>
                  {t('common:refresh')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(requests, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('common:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('common:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('common:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('common:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('common:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Process Request Drawer */}
      <Drawer
        anchor="right"
        open={processDrawer.open}
        onClose={() => setProcessDrawer({ open: false, sid: null })}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold', mb: 2 }}>
            {t('fulfillment:requests.processRequest')}
          </Typography>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : requestDetail ? (
            <>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                {requestDetail.request_number}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {getUserIdentity(requestDetail.user)}
                {requestDetail.user?.bin_code ? ` - BIN: ${requestDetail.user.bin_code}` : ''}
                {` - ${requestDetail.service_type?.name || 'N/A'}`}
              </Typography>

              {requestDetail.package_info && (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      endIcon={<OpenInNew fontSize="small" />}
                      onClick={() => window.open(`/admin/packages?search=${encodeURIComponent(requestDetail.package_info.package_number)}`, '_blank')}
                    >
                      Open
                    </Button>
                  }
                >
                  Linked package: {requestDetail.package_info.package_number}
                </Alert>
              )}

              <Box sx={{ mt: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Tracking Items
                  </Typography>
                  <Button size="small" startIcon={<Add />} onClick={addFormItem}>
                    Add
                  </Button>
                </Stack>
                {formItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Item {index + 1}
                      </Typography>
                      {formItems.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => removeFormItem(index)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tracking Number"
                      value={item.tracking_number}
                      onChange={(e) => updateFormItem(index, { tracking_number: e.target.value })}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Local Logistics Company"
                      value={item.local_logistics_company_name}
                      onChange={(e) => updateFormItem(index, { local_logistics_company_name: e.target.value })}
                      sx={{ mb: 1 }}
                    />
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Items"
                          value={item.items_count}
                          onChange={(e) => updateFormItem(index, { items_count: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Packages"
                          value={item.package_count}
                          onChange={(e) => updateFormItem(index, { package_count: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Weight"
                          value={item.package_weight}
                          onChange={(e) => updateFormItem(index, { package_weight: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Dimensions"
                          value={item.package_dimensions}
                          onChange={(e) => updateFormItem(index, { package_dimensions: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Goods"
                          value={item.goods_type}
                          onChange={(e) => updateFormItem(index, { goods_type: e.target.value })}
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="special">Special</MenuItem>
                          <MenuItem value="mixed">Mixed</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Condition"
                          value={item.condition}
                          onChange={(e) => updateFormItem(index, { condition: e.target.value })}
                        >
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="used">Used</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Receive Status"
                            value={item.status || 'pending_receive'}
                            onChange={(e) => updateFormItem(index, { status: e.target.value })}
                          >
                            <MenuItem value="pending_receive">Pending Receive</MenuItem>
                            <MenuItem value="received">Received</MenuItem>
                          </TextField>
                          {item.sid && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSaveItemStatus(index)}
                              sx={{ minWidth: 110 }}
                            >
                              Save Status
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={uploadingItemImage[item.sid] ? <CircularProgress size={16} /> : <CloudUpload />}
                            disabled={!item.sid || !!uploadingItemImage[item.sid]}
                          >
                            Upload Receive Proof
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadItemReceivedImage(index, e)}
                            />
                          </Button>
                          {!item.sid && (
                            <Typography variant="caption" color="text.secondary">
                              Save this request before uploading item proof.
                            </Typography>
                          )}
                        </Stack>
                        {Array.isArray(item.received_images) && item.received_images.length > 0 && (
                          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                            {item.received_images.map((url, photoIndex) => (
                              <Chip
                                key={`${url}-${photoIndex}`}
                                label={`Proof ${photoIndex + 1}`}
                                size="small"
                                variant="outlined"
                                onClick={() => window.open(url, '_blank')}
                              />
                            ))}
                          </Stack>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Seller / Shop"
                          value={item.seller_name}
                          onChange={(e) => updateFormItem(index, { seller_name: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Platform"
                          value={item.seller_platform}
                          onChange={(e) => updateFormItem(index, { seller_platform: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Order No."
                          value={item.seller_order_number}
                          onChange={(e) => updateFormItem(index, { seller_order_number: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>

              <InputLabel sx={{ mt: 2, mb: 0.5 }}>{t('fulfillment:requests.updateStatus')}</InputLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value)}
                sx={{ mb: 1 }}
              >
                <MenuItem value="pending_payment">{t('fulfillment:requests.statuses.pending_payment')}</MenuItem>
                <MenuItem value="pending_receive">{t('fulfillment:requests.statuses.pending_receive')}</MenuItem>
                <MenuItem value="received">{t('fulfillment:requests.statuses.received')}</MenuItem>
                <MenuItem value="ready_to_package">{t('fulfillment:requests.statuses.ready_to_package')}</MenuItem>
                <MenuItem value="inspecting">{t('fulfillment:requests.statuses.inspecting')}</MenuItem>
                <MenuItem value="packaged">{t('fulfillment:requests.statuses.packaged')}</MenuItem>
                <MenuItem value="completed">{t('fulfillment:requests.statuses.completed')}</MenuItem>
                <MenuItem value="cancelled">{t('fulfillment:requests.statuses.cancelled')}</MenuItem>
              </TextField>

              {formStatus === 'packaged' && (
                <>
                  <InputLabel sx={{ mt: 2, mb: 0.5 }} required>{t('fulfillment:requests.packageWeight')}</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 0.01, step: 0.01 }}
                    value={formPackageWeight}
                    onChange={(e) => setFormPackageWeight(e.target.value)}
                    placeholder="e.g. 2.5"
                    sx={{ mb: 1 }}
                  />
                  <InputLabel sx={{ mt: 2, mb: 0.5 }} required>{t('fulfillment:requests.packageDimensions')}</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    value={formPackageDimensions}
                    onChange={(e) => setFormPackageDimensions(e.target.value)}
                    placeholder="e.g. 50x40x30"
                    sx={{ mb: 1 }}
                  />
                </>
              )}

              <InputLabel sx={{ mt: 2, mb: 0.5 }}>{t('fulfillment:requests.adminNotes')}</InputLabel>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                value={formAdminNotes}
                onChange={(e) => setFormAdminNotes(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveStatus}
                disabled={savingStatus}
                sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, mb: 3 }}
              >
                {savingStatus ? <CircularProgress size={24} /> : t('common:save')}
              </Button>

              {requestDetail.service_type?.requires_inspection !== false && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    {t('fulfillment:requests.inspectionImages')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {(requestDetail.inspection_images || []).map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                        <Box component="img" src={url} alt="" sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }} />
                      </a>
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    startIcon={uploadingInspection ? <CircularProgress size={16} /> : <CloudUpload />}
                    disabled={uploadingInspection}
                  >
                    {t('fulfillment:requests.uploadInspectionImage')}
                    <input type="file" accept="image/*" hidden onChange={handleUploadInspection} />
                  </Button>
                </>
              )}
              {requestDetail.service_type?.requires_inspection === false && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1, mb: 1 }}>
                  {t('fulfillment:requests.onlyPackageImage')}
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                {t('fulfillment:requests.packageImage')}
              </Typography>
              {requestDetail.package_image && (
                <Box sx={{ mb: 1 }}>
                  <a href={requestDetail.package_image} target="_blank" rel="noopener noreferrer">
                    <Box component="img" src={requestDetail.package_image} alt="Package" sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, border: '1px solid #ddd' }} />
                  </a>
                </Box>
              )}
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={uploadingPackage ? <CircularProgress size={16} /> : <CloudUpload />}
                disabled={uploadingPackage}
              >
                {t('fulfillment:requests.uploadPackageImage')}
                <input type="file" accept="image/*" hidden onChange={handleUploadPackage} />
              </Button>
            </>
          ) : null}
        </Box>
      </Drawer>

      {/* Recalculate Dialog */}
      <Dialog open={recalculateDialog.open} onClose={() => setRecalculateDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('fulfillment:requests.recalculateFees')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to recalculate fees for request {recalculateDialog.request?.request_number}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalculateDialog({ open: false, request: null })}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleRecalculateConfirm}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('fulfillment:requests.recalculateFees')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Requests;
